// Shared types + helpers for the Übergabeprotokoll mobile flow.
// Tables don't exist in supabase-types.ts yet — use the typed client cast at call sites.

export type ProtocolKind = "move_in" | "move_out";
export type ProtocolStatus = "draft" | "submitted";
export type CommonArea = "kitchen" | "bath" | "hallway" | "other";
export type PhotoCategory = "room" | CommonArea | "keys";
export type ItemCondition = "good" | "wear" | "damaged";
export type Severity = "low" | "medium" | "high";

export type Protocol = {
  id: string;
  ref: string;
  booking_id: number;
  user_id: string | null;
  kind: ProtocolKind;
  status: ProtocolStatus;
  signature_data: string | null;
  signed_at: string | null;
  submitted_at: string | null;
  /** Storage path of the rendered PDF (COL-211 / COL-214). Null until
   *  DoneStep has uploaded the client-rendered PDF. */
  pdf_storage_path: string | null;
  /** When the tenant + admin confirmation email was last sent
   *  successfully. Null while the email is still pending. */
  email_sent_at: string | null;
  created_at: string;
  updated_at: string;
  /** Admin who recorded this protocol on the tenant's behalf; null for a
   *  tenant walkthrough. Optional so rows from a database without the
   *  20260818100000 migration still type-check. */
  recorded_by_user_id?: string | null;
  recorded_by_name?: string | null;
  /** When the admin filed the record — distinct from `submitted_at`, which
   *  carries the (often backdated) date of the walkthrough itself. */
  recorded_at?: string | null;
  /** The admin's statement: what was recorded, and why the tenant did not
   *  submit it. Printed inside the generated PDF. */
  admin_note?: string | null;
  /** Set when the submission window closed with no walkthrough and the tick
   *  deemed the room accepted (20260820150000). NULL means a real submission
   *  — by the tenant, or filed by an admin per `recorded_by_name` above. */
  auto_submitted_at?: string | null;
};

/**
 * Was this protocol filed by an admin rather than walked through by the tenant?
 *
 * Reads BOTH attribution columns on purpose: `recorded_by_user_id` is
 * `ON DELETE SET NULL`, so deleting the admin's account would otherwise turn
 * their recording back into an apparent tenant submission — the one direction
 * this flag must never move. `recorded_by_name` is denormalised and survives.
 */
export function isAdminRecorded(
  p:
    | Pick<Protocol, "recorded_by_user_id" | "recorded_by_name">
    | null
    | undefined
): boolean {
  return !!p?.recorded_by_user_id || !!p?.recorded_by_name;
}

/**
 * Was this protocol deemed submitted by the tick rather than walked through?
 *
 * Distinct from `isAdminRecorded`: nobody filed anything here either — the
 * window simply closed. Every reader that shows this protocol as evidence of
 * a walkthrough (the tenant profile, the deposit bundle cover sheet) must
 * check this before claiming a tenant signed it.
 */
export function isAutoSubmitted(
  p: Pick<Protocol, "auto_submitted_at"> | null | undefined
): boolean {
  return !!p?.auto_submitted_at;
}

/**
 * When this protocol record was actually FILED.
 *
 * Deliberately NOT `submitted_at` first. For a protocol an admin recorded on
 * the tenant's behalf, `submitted_at` carries the date of the WALKTHROUGH —
 * which admins routinely backdate, because they are recording something that
 * happened last week. `recorded_at` is the moment the record was filed. Any
 * reader comparing a protocol against a later event (a reopen, an audit
 * cut-off) needs the filing moment, or a record filed minutes ago reads as
 * weeks old. A tenant walkthrough has only `submitted_at`; a window that
 * lapsed unsubmitted has only `auto_submitted_at`.
 */
export function protocolFiledAt(
  p:
    | Pick<Protocol, "recorded_at" | "submitted_at" | "auto_submitted_at">
    | null
    | undefined
): string | null {
  return p?.recorded_at ?? p?.submitted_at ?? p?.auto_submitted_at ?? null;
}

/**
 * Has an admin's reopen superseded this submitted protocol?
 *
 * An admin reopening a protocol is saying "this record does not stand; let
 * them do it properly". So a submission filed BEFORE the reopen stops
 * counting as the protocol for its kind — the tenant gets their walkthrough
 * back — while one filed AFTER the reopen is the answer to it and stands.
 *
 * Shared on purpose. The rule started life inside the tenant protocol card;
 * the admin tenant profile and `protocol-reminder-tick` each carried their own
 * "any submitted row is final" reading, so the same tenancy read as reopened
 * on one screen and closed on the next. One rule, three callers. The edge
 * function cannot import from `src/` — its copy lives in
 * `supabase/functions/protocol-reminder-tick/window-helpers.ts` and must move
 * with this one.
 *
 * Superseding hides a record from the surfaces that ask "is this kind done?".
 * It never deletes anything: the row and its PDF stay exactly where they are.
 */
export function isSupersededByReopen(
  p:
    | Pick<
        Protocol,
        "status" | "recorded_at" | "submitted_at" | "auto_submitted_at"
      >
    | null
    | undefined,
  /** `bookings.move_{in,out}_protocol_reopened_at` for this protocol's kind. */
  reopenedAt: string | null | undefined
): boolean {
  if (!reopenedAt || !p || p.status !== "submitted") return false;
  const filedAt = protocolFiledAt(p);
  // Submitted but carrying no timestamp at all: nothing can date it after the
  // reopen, so the reopen wins.
  if (!filedAt) return true;
  const filed = Date.parse(filedAt);
  const reopened = Date.parse(reopenedAt);
  // An unparseable stamp must not silently retire a filed protocol.
  if (Number.isNaN(filed) || Number.isNaN(reopened)) return false;
  return filed < reopened;
}

/**
 * Is this a handover record somebody actually filed?
 *
 * The tick's deemed-accepted placeholder carries `status = 'submitted'` so
 * every document surface treats it as filed — correctly, it is the artefact
 * the contract clause rests on. But nobody walked through it, so it must not
 * count as the walkthrough itself. Two things turn on the difference:
 *
 *   • An admin reopening the window is asking for precisely the walkthrough
 *     that never happened, so a placeholder must not block the tenant.
 *   • Where several protocols exist for one kind, the real one is the one
 *     every card and PDF link should speak for.
 *
 * A recording an admin filed on the tenant's behalf IS real — someone
 * attested to it under their own name. Only the tick's placeholder is not.
 */
export function isRealSubmission(
  p:
    | (Pick<Protocol, "auto_submitted_at"> & { status?: string | null })
    | null
    | undefined
): boolean {
  return p?.status === "submitted" && !isAutoSubmitted(p);
}

export type ProtocolPhoto = {
  id: string;
  protocol_id: string;
  category: PhotoCategory;
  storage_path: string;
  ordering: number;
  created_at: string;
};

export type ProtocolInventoryItem = {
  id: string;
  protocol_id: string;
  label: string;
  meta: string | null;
  icon: string | null;
  ordering: number;
  condition: ItemCondition | null;
  photo_storage_path: string | null;
  defect_description: string | null;
  defect_severity: Severity | null;
};

export type ProtocolDefect = {
  id: string;
  protocol_id: string;
  area: CommonArea;
  description: string;
  severity: Severity;
  photo_storage_path: string | null;
};

/** COL-425: evidence an admin attached when filing a protocol on the tenant's behalf. */
export type ProtocolAttachment = {
  id: string;
  protocol_id: string;
  storage_path: string;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
};

export const PROTOCOL_STORAGE_BUCKET = "handover-protocols";
export const MIN_ROOM_PHOTOS = 3;
export const MIN_COMMON_AREA_PHOTOS = 2;

/** UP-YYYY-XXXXXX — visible reference shown on the success screen. */
export function generateProtocolRef(): string {
  const year = new Date().getFullYear();
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let suffix = "";
  for (let i = 0; i < 6; i++) {
    suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `UP-${year}-${suffix}`;
}

/** Seed inventory list used when a protocol is first created. Mirrors the mockup. */
export const DEFAULT_INVENTORY: Array<
  Pick<ProtocolInventoryItem, "label" | "meta" | "icon" | "ordering">
> = [
  { label: "Bett", meta: "140×200, Lattenrost", icon: "bed", ordering: 0 },
  { label: "Schreibtisch", meta: "Buche, 120×60", icon: "desk", ordering: 1 },
  { label: "Stuhl", meta: "Bürostuhl, schwarz", icon: "chair", ordering: 2 },
  { label: "Regal", meta: "5 Fächer, weiß", icon: "shelf", ordering: 3 },
  { label: "Deckenlampe", meta: "LED, dimmbar", icon: "lamp", ordering: 4 },
];

export const COMMON_AREAS: Array<{
  id: CommonArea;
  label: string;
}> = [
  { id: "kitchen", label: "Küche" },
  { id: "bath", label: "Bad" },
  { id: "hallway", label: "Flur" },
  { id: "other", label: "Sonstiges" },
];

/**
 * First required common area (Kitchen / Bath / Hallway, in that order) that
 * still lacks its minimum photo count, or null once all three are done.
 *
 * "Other" is never required (COL-433) — no tenant needs a hint to fill it.
 * Order matches COMMON_AREAS / the step's tab order, so the hint always
 * points at whichever tab the tenant would hit first.
 */
export function getMissingCommonArea(
  photos: { category: string }[]
): Exclude<CommonArea, "other"> | null {
  const counts: Record<string, number> = {};
  for (const p of photos) {
    if (p.category !== "room")
      counts[p.category] = (counts[p.category] ?? 0) + 1;
  }
  const required: Array<Exclude<CommonArea, "other">> = [
    "kitchen",
    "bath",
    "hallway",
  ];
  return (
    required.find((a) => (counts[a] ?? 0) < MIN_COMMON_AREA_PHOTOS) ?? null
  );
}

export const SEVERITY_LABEL: Record<Severity, string> = {
  low: "Gering",
  medium: "Mittel",
  high: "Schwerwiegend",
};

export const CONDITION_LABEL: Record<ItemCondition, string> = {
  good: "Gut",
  wear: "Gebrauchsspuren",
  damaged: "Beschädigt",
};

export function formatDateDE(d: string | Date): string {
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
